package com.patronat.top.features.venues.presentation.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.patronat.top.features.venues.presentation.MapTilerAddressResult
import com.patronat.top.features.venues.presentation.MapLibreConfig
import com.patronat.top.features.venues.presentation.MapTilerConfiguration
import com.patronat.top.features.venues.presentation.MapLibreVenueMap
import com.patronat.top.features.venues.presentation.VenueMapMarker
import com.patronat.top.features.venues.presentation.isMapLibreSupported
import com.patronat.top.features.venues.presentation.reverseMapTilerGeocode
import com.patronat.top.features.venues.presentation.searchMapTilerAddresses
import com.patronat.top.ui.atoms.inputs.InputState
import com.patronat.top.ui.models.VenueLocationData
import com.patronat.top.ui.organisms.location.LocationSection
import com.patronat.top.ui.theme.BodyMedium
import com.patronat.top.ui.theme.MineShaft100
import com.patronat.top.ui.theme.MineShaft500
import com.patronat.top.ui.theme.MineShaft900
import com.patronat.top.ui.theme.White
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import top.features.venues.generated.resources.Res
import top.features.venues.generated.resources.*

@Composable
fun VenueLocationSection(
    data: VenueLocationData,
    onDataChange: (VenueLocationData) -> Unit,
    addressError: Boolean = false,
    modifier: Modifier = Modifier,
    title: String,
    leadingIcon: Painter? = null,
    addressLabel: String,
    addressPlaceholder: String,
    venueMarkers: List<VenueMapMarker> = emptyList(),
    onMarkerClicked: (VenueMapMarker) -> Unit = {}
) {
    var addressSuggestions by remember { mutableStateOf<List<MapTilerAddressResult>>(emptyList()) }
    var isSearchLoading by remember { mutableStateOf(false) }
    var searchJob by remember { mutableStateOf<Job?>(null) }
    val coroutineScope = rememberCoroutineScope()

    val mapLibreConfig = remember {
        MapLibreConfig(
            apiKey = MapTilerConfiguration.API_KEY,
            style = MapTilerConfiguration.DEFAULT_STYLE
        )
    }

    // Use generic LocationSection from design system
    LocationSection(
        address = data.address,
        onAddressChange = { newAddress ->
            onDataChange(data.copy(address = newAddress))

            // MapTiler address search
            searchJob?.cancel()
            if (newAddress.isNotBlank() && newAddress.length > 2) {
                searchJob = coroutineScope.launch {
                    delay(300) // Debouncing
                    isSearchLoading = true

                    val results = searchMapTilerAddresses(
                        query = newAddress,
                        apiKey = MapTilerConfiguration.API_KEY,
                        proximity = data.latitude?.let { lat ->
                            data.longitude?.let { lng ->
                                Pair(lat, lng)
                            }
                        },
                        countryCode = "UA",
                        limit = 8
                    )

                    addressSuggestions = results
                    isSearchLoading = false
                }
            } else {
                addressSuggestions = emptyList()
                isSearchLoading = false
            }
        },
        modifier = modifier,
        title = title,
        leadingIcon = leadingIcon,
        addressLabel = addressLabel,
        addressPlaceholder = addressPlaceholder,
        addressState = if (addressError) InputState.Error else null,
        suggestionsContent = if (isSearchLoading || addressSuggestions.isNotEmpty()) {
            {
                MapTilerSuggestionsContent(
                    suggestions = addressSuggestions,
                    isLoading = isSearchLoading,
                    onSuggestionSelected = { suggestion ->
                        onDataChange(
                            data.copy(
                                address = suggestion.placeName,
                                latitude = suggestion.latitude,
                                longitude = suggestion.longitude
                            )
                        )
                        addressSuggestions = emptyList()
                        searchJob?.cancel()
                    }
                )
            }
        } else null,
        mapContent = if (isMapLibreSupported()) {
            {
                MapLibreVenueMap(
                    selectedLatitude = data.latitude,
                    selectedLongitude = data.longitude,
                    mapLibreConfig = mapLibreConfig,
                    // Venue form map must stay clean (without nearby venue markers).
                    venueMarkers = emptyList(),
                    onPointSelected = { lat, lng ->
                        onDataChange(
                            data.copy(
                                latitude = lat,
                                longitude = lng
                            )
                        )

                        // Reverse geocoding
                        coroutineScope.launch {
                            val reverseResult = reverseMapTilerGeocode(
                                latitude = lat,
                                longitude = lng,
                                apiKey = MapTilerConfiguration.API_KEY
                            )

                            reverseResult?.let { result ->
                                onDataChange(
                                    data.copy(
                                        address = result.placeName,
                                        latitude = lat,
                                        longitude = lng
                                    )
                                )
                            }
                        }
                    },
                    onMarkerClicked = onMarkerClicked,
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(300.dp),
                    zoom = 15.0,
                    interactive = true
                )
            }
        } else null
    )
}

@Composable
private fun MapTilerSuggestionsContent(
    suggestions: List<MapTilerAddressResult>,
    isLoading: Boolean,
    onSuggestionSelected: (MapTilerAddressResult) -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .border(1.dp, MineShaft100, RoundedCornerShape(8.dp))
            .background(White)
            .heightIn(max = 200.dp)
            .verticalScroll(rememberScrollState())
    ) {
        if (isLoading) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(12.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                CircularProgressIndicator(
                    modifier = Modifier
                        .padding(end = 8.dp)
                        .size(16.dp),
                    strokeWidth = 2.dp
                )
                Text(
                    text = stringResource(Res.string.venue_location_loading),
                    style = BodyMedium,
                    color = MineShaft500
                )
            }
        }

        suggestions.forEachIndexed { index, suggestion ->
            if (index > 0) {
                HorizontalDivider(color = MineShaft100)
            }

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .clickable { onSuggestionSelected(suggestion) }
                    .padding(12.dp)
            ) {
                Text(
                    text = suggestion.placeName,
                    style = BodyMedium,
                    color = MineShaft900,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis
                )

                if (suggestion.placeType.isNotEmpty()) {
                    Text(
                        text = stringResource(
                            Res.string.venue_location_type_format,
                            suggestion.placeType.joinToString(", ")
                        ),
                        style = BodyMedium,
                        color = MineShaft500,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }

                Text(
                    text = stringResource(
                        Res.string.venue_location_relevance_format,
                        (suggestion.relevance * 100).toInt()
                    ),
                    style = BodyMedium,
                    color = MineShaft500
                )
            }
        }
    }
}
